/**
 * Request Detail Page
 *
 * Displays full details of a single request including all answers organized by sections
 * Supports URL field types with clickable links that open in new tabs
 */

import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import api from '../services/api';
import { ROUTES } from '../config/constants';
import { formatDate, getStatusLabel } from '../utils/formatters';
import { TYPE_CODES, TYPE_DISPLAY_NAMES, TYPE_VERIFIED_DISPLAY_OVERRIDE } from '../config/requestTypeConstants';
import FieldDelegationModal from '../components/IntakeFlow/FieldDelegationModal';
import DelegateInputPanel from '../components/IntakeFlow/DelegateInputPanel';
import DelegationHistoryPanel from '../components/IntakeFlow/DelegationHistoryPanel';
import './RequestDetail.css';

const RequestDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [request, setRequest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [fieldChanges, setFieldChanges] = useState(null);
  const [loadingChanges, setLoadingChanges] = useState(false);
  const [changesError, setChangesError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Delegation state
  const [showDelegationModal, setShowDelegationModal] = useState(false);
  const [delegationRound, setDelegationRound] = useState(null);
  const [delegationHistory, setDelegationHistory] = useState([]);
  const [cancellingDelegation, setCancellingDelegation] = useState(false);
  const [delegationSubmitSuccess, setDelegationSubmitSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // NPR admin actions state
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [nprActionLoading, setNprActionLoading] = useState(false);
  const [nprActionError, setNprActionError] = useState(null);
  const [createProductForm, setCreateProductForm] = useState({
    product_name: '',
    product_type: '',
    release_type: '',
    product_code: '',
    description: '',
  });

  useEffect(() => {
    fetchRequest();
    api.getCurrentUser()
      .then(res => setCurrentUser(res.data.data.user || res.data.data))
      .catch(() => {});
    fetchDelegationHistory();
  }, [id]);

  useEffect(() => {
    if (request && isCuCRequest(request)) {
      fetchFieldChanges();
    }
  }, [request]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      const response = await api.getRequestById(id);
      const requestData = response.data.data.request;
      setRequest(requestData);

      // Load questions for this request type — use type_code directly
      let formType = requestData.requestType?.type_code;

      // For legacy GPR records, check gpr_subtype answer to resolve subtype
      if (formType === TYPE_CODES.GPR && requestData.answers) {
        const gprSubtypeAnswer = requestData.answers.find(a => a.question_key === 'gpr_subtype');
        if (gprSubtypeAnswer) {
          const { GPR_SUBTYPE_ANSWER_TO_CODE } = require('../config/requestTypeConstants');
          formType = GPR_SUBTYPE_ANSWER_TO_CODE[gprSubtypeAnswer.answer_value] || formType;
        }
      }

      if (formType) {
        try {
          const questionsResponse = await api.getQuestions(formType);
          const loadedQuestions = questionsResponse.data.data.questions || [];
          setQuestions(loadedQuestions);
        } catch (err) {
          console.error('Error fetching questions:', err);
        }
      }
    } catch (err) {
      console.error('Error fetching request:', err);
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    history.push(`/intake/${id}`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this request?')) {
      return;
    }

    try {
      await api.deleteRequest(id);
      history.push(ROUTES.DASHBOARD);
    } catch (err) {
      console.error('Error deleting request:', err);
      alert('Failed to delete request: ' + err.message);
    }
  };

  const fromReporting = location.state?.from === 'reporting';

  const handleBack = () => {
    if (fromReporting) {
      history.goBack();
    } else {
      history.push(ROUTES.DASHBOARD);
    }
  };

  // Check if request is CuC type
  const isCuCRequest = (req) => {
    if (!req) return false;
    return req.requestType?.type_code === TYPE_CODES.CUC;
  };

  const isNPRRequest = (req) => {
    if (!req) return false;
    return req.requestType?.type_code === TYPE_CODES.NEW_PRODUCT_REQUEST;
  };

  const getAnswerByKey = (req, key) => {
    if (!req?.answers) return '';
    const ans = req.answers.find(a => a.question_key === key);
    return ans?.answer_value || '';
  };

  const openCreateProductModal = (req) => {
    setCreateProductForm({
      product_name: getAnswerByKey(req, 'npr_product_name'),
      product_type: getAnswerByKey(req, 'npr_product_type'),
      release_type: getAnswerByKey(req, 'npr_release_type'),
      product_code: getAnswerByKey(req, 'npr_product_code'),
      description: getAnswerByKey(req, 'npr_description'),
    });
    setNprActionError(null);
    setShowCreateProductModal(true);
  };

  const handleCreateProduct = async () => {
    setNprActionLoading(true);
    setNprActionError(null);
    try {
      await api.createProductFromRequest(id, createProductForm);
      setShowCreateProductModal(false);
      alert('Product created successfully. An email has been sent to the requester.');
      fetchRequest();
    } catch (err) {
      setNprActionError(err.message || 'Failed to create product');
    } finally {
      setNprActionLoading(false);
    }
  };

  const handleDeclineRequest = async () => {
    if (!declineReason.trim()) {
      setNprActionError('Please provide a reason for declining.');
      return;
    }
    setNprActionLoading(true);
    setNprActionError(null);
    try {
      await api.rejectProductRequest(id, declineReason.trim());
      setShowDeclineModal(false);
      setDeclineReason('');
      alert('Request declined. An email has been sent to the requester.');
      fetchRequest();
    } catch (err) {
      setNprActionError(err.message || 'Failed to decline request');
    } finally {
      setNprActionLoading(false);
    }
  };

  // Fetch field changes for CuC requests
  const fetchFieldChanges = async () => {
    try {
      setLoadingChanges(true);
      setChangesError(null);
      const response = await api.getRequestFieldChanges(id);
      setFieldChanges(response.data.data);
    } catch (err) {
      console.error('Error fetching field changes:', err);
      setChangesError(err.message);
    } finally {
      setLoadingChanges(false);
    }
  };

  const fetchDelegationHistory = async () => {
    try {
      const res = await api.getDelegationHistory(id);
      const rounds = res.data.data.rounds || [];
      setDelegationHistory(rounds);
      const active = rounds.find(r => r.status === 'pending');
      setDelegationRound(active || null);
    } catch {
      // delegation history is optional; ignore errors
    }
  };

  const handleDelegationInitiated = () => {
    setShowDelegationModal(false);
    fetchRequest();
    fetchDelegationHistory();
  };

  const handleDelegationSubmitted = () => {
    setDelegationSubmitSuccess(true);
    // Delay refresh so the success message is visible before the panel unmounts
    setTimeout(() => {
      fetchRequest();
      fetchDelegationHistory();
    }, 2000);
  };

  const handleCancelDelegation = async () => {
    if (!window.confirm('Cancel contributions? This will return the request to Draft and discard all pending input.')) return;
    setCancellingDelegation(true);
    try {
      await api.cancelDelegation(id);
      fetchRequest();
      fetchDelegationHistory();
    } catch (err) {
      alert('Failed to cancel delegation: ' + (err.response?.data?.message || err.message));
    } finally {
      setCancellingDelegation(false);
    }
  };

  const DELEGATION_ENABLED_TYPES = new Set([TYPE_CODES.PRIVACY, TYPE_CODES.SOR, TYPE_CODES.PRICING_PACKAGING]);
  const isDelegationEnabled = (req) => DELEGATION_ENABLED_TYPES.has(req?.requestType?.type_code);
  const canManageDelegation = (user) => user && (user.role === 'admin' || user.role === 'approver');

  // Get background color for a field based on change indicator
  const getFieldBackgroundColor = (questionKey) => {
    if (!fieldChanges || !fieldChanges.fieldComparisons) return '';

    const comparison = fieldChanges.fieldComparisons[questionKey];
    if (!comparison) return '';

    switch (comparison.changeIndicator) {
      case '✓': // Changed
        return '#e8f5e9'; // Light green
      case '🕐': // New product
        return '#e3f2fd'; // Light blue
      case '❓': // Cannot compare
        return '#f5f5f5'; // Light gray
      default: // ✗ (unchanged)
        return '';
    }
  };

  // Get change indicator symbol and details for a field
  const getFieldChangeInfo = (questionKey) => {
    if (!fieldChanges || !fieldChanges.fieldComparisons) return null;

    const comparison = fieldChanges.fieldComparisons[questionKey];
    if (!comparison) return null;

    return {
      indicator: comparison.changeIndicator,
      currentValue: comparison.currentValue,
      priorValue: comparison.priorValue,
      hasChanged: comparison.hasChanged,
      isExcluded: comparison.isExcluded,
      isNewField: comparison.isNewField
    };
  };

  // Get tooltip text for change indicator
  const getChangeTooltip = (questionKey) => {
    const info = getFieldChangeInfo(questionKey);
    if (!info) return '';

    if (info.indicator === '🕐') {
      return fieldChanges.isNewProduct
        ? 'New product (no prior release data)'
        : 'New field in this release';
    }

    if (info.indicator === '❓') {
      return 'Cannot compare with prior release';
    }

    if (info.indicator === '✓') {
      // Format values for display
      const formatValue = (val) => {
        if (val === null || val === undefined || val === '') return '(empty)';
        if (typeof val === 'string' && val.length > 50) return val.substring(0, 50) + '...';
        return String(val);
      };
      return `Changed from: ${formatValue(info.priorValue)}\nTo: ${formatValue(info.currentValue)}`;
    }

    if (info.isExcluded) {
      return 'Metadata field (not compared)';
    }

    return 'No change from prior release';
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      Draft: 'request-detail__badge--gray',
      in_progress: 'request-detail__badge--blue',
      submitted: 'request-detail__badge--yellow',
      completed: 'request-detail__badge--green',
      'Needs Input': 'request-detail__badge--orange',
    };
    return statusMap[status] || 'request-detail__badge--gray';
  };

  // Group questions by section
  const groupQuestionsBySection = () => {
    if (questions.length === 0) return [];

    const hasSections = questions.some(q => q.section);

    if (hasSections) {
      // Group by section for forms that have sections
      const sectionMap = new Map();

      questions.forEach(q => {
        const section = q.section || 'Other Questions';
        if (!sectionMap.has(section)) {
          sectionMap.set(section, []);
        }
        sectionMap.get(section).push(q);
      });

      // Convert to array and sort questions within each section by display_order
      return Array.from(sectionMap.entries()).map(([sectionName, sectionQuestions]) => ({
        name: sectionName,
        questions: sectionQuestions.sort((a, b) => a.display_order - b.display_order)
      }));
    } else {
      // Single section if no sections defined
      return [{
        name: 'Intake Responses',
        questions: questions.sort((a, b) => a.display_order - b.display_order)
      }];
    }
  };

  // Get answer value for a question
  const getAnswerValue = (questionKey) => {
    if (!request?.answers) {
      return 'No answer provided';
    }

    const answer = request.answers.find(a => a.question_key === questionKey);
    if (!answer) {
      return 'No answer provided';
    }

    if (!answer.answer_value) {
      return 'No answer provided';
    }

    const value = answer.answer_value;

    // Handle array values (multi-select)
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'No answer provided';
    }

    // Handle object values (JSON)
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return value;
  };

  // Render answer value with special handling for URL fields
  const renderAnswerValue = (question, value) => {
    if (!value || value === 'No answer provided') {
      return <span className="request-detail__value--empty">{value || 'No answer provided'}</span>;
    }

    // Ensure value is a string for processing
    const stringValue = String(value);

    // Check if this is a URL field
    if (question.field_type === 'url') {
      // First, try to validate if the entire value is a URL
      try {
        new URL(stringValue);
        return (
          <a
            href={stringValue}
            target="_blank"
            rel="noopener noreferrer"
            className="request-detail__link"
          >
            <svg className="request-detail__link-icon" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
              <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
            </svg>
            {stringValue}
          </a>
        );
      } catch (e) {
        // Not a pure URL, try to extract URLs from the text
        const urlRegex = /(https?:\/\/[^\s]+)/gi;
        const urls = stringValue.match(urlRegex);

        if (urls && urls.length > 0) {
          // Split text by URLs and create a mix of text and links
          const parts = stringValue.split(urlRegex);
          return (
            <span>
              {parts.map((part, index) => {
                // Check if this part is a URL
                if (urls.includes(part)) {
                  return (
                    <a
                      key={index}
                      href={part}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="request-detail__link"
                    >
                      <svg className="request-detail__link-icon" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                        <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                      </svg>
                      {part}
                    </a>
                  );
                }
                // Regular text
                return <span key={index}>{part}</span>;
              })}
            </span>
          );
        }

        // No URLs found, show as plain text
        return <span>{stringValue}</span>;
      }
    }

    return <span>{stringValue}</span>;
  };

  const sections = groupQuestionsBySection();

  if (loading) {
    return (
      <div className="request-detail__loading">
        <div className="request-detail__spinner" />
        <p>Loading request details...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="request-detail__error">
        <h2>Request Not Found</h2>
        <p>This request has been deleted or no longer exists. If you arrived here from an email notification, the request may have been withdrawn by the submitter.</p>
        <button onClick={() => history.push('/')} className="request-detail__button">
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="request-detail__error">
        <h2>Error Loading Request</h2>
        <p>{error || 'Request not found'}</p>
        <button onClick={handleBack} className="request-detail__button">
          {fromReporting ? 'Back to Reporting' : 'Back to Dashboard'}
        </button>
      </div>
    );
  }

  return (
    <div className="request-detail">
      {/* Header */}
      <div className="request-detail__header">
        <div className="request-detail__header-top">
          <button onClick={handleBack} className="request-detail__back-btn">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            {fromReporting ? 'Back to Reporting' : 'Back to Dashboard'}
          </button>
          <div className="request-detail__actions">
            {/* Delegation button: admin/approver + Draft + delegation-enabled type */}
            {isDelegationEnabled(request) && request.status === 'Draft' && canManageDelegation(currentUser) && (
              <button
                onClick={() => setShowDelegationModal(true)}
                className="request-detail__button request-detail__button--secondary"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v1h8v-1zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-1a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v1h-3zM4.75 14.094A5.973 5.973 0 004 17v1H1v-1a3 3 0 013.75-2.906z" />
                </svg>
                Request Contributions
              </button>
            )}
            {/* Edit: hidden while Needs Input and for completed NPR */}
            {request.status !== 'Needs Input' && !(isNPRRequest(request) && request.status === 'Verified') && (
              <button
                onClick={handleEdit}
                className="request-detail__button request-detail__button--secondary"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Edit Request
              </button>
            )}
            {request.status === 'Draft' && (
              <button
                onClick={handleDelete}
                className="request-detail__button request-detail__button--danger"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Delete
              </button>
            )}
          </div>
        </div>

        <div className="request-detail__title-row">
          <div>
            <h1 className="request-detail__title">{request.request_number}</h1>
            <p className="request-detail__subtitle">{TYPE_DISPLAY_NAMES[request.requestType?.type_code] || request.requestType?.type_name || request.request_type}</p>
          </div>
          <span className={`request-detail__badge ${getStatusBadgeClass(request.status)}`}>
            {request.status === 'Verified' && TYPE_VERIFIED_DISPLAY_OVERRIDE[request.requestType?.type_code]
              ? TYPE_VERIFIED_DISPLAY_OVERRIDE[request.requestType.type_code]
              : getStatusLabel(request.status)}
          </span>
        </div>
      </div>

      {/* Tabs — only for delegation-enabled request types */}
      {isDelegationEnabled(request) && (
        <div className="request-detail__tabs">
          <button
            className={`request-detail__tab${activeTab === 'details' ? ' request-detail__tab--active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Request Details
          </button>
          <button
            className={`request-detail__tab${activeTab === 'delegation' ? ' request-detail__tab--active' : ''}`}
            onClick={() => setActiveTab('delegation')}
          >
            Contributions
            {delegationRound && <span className="request-detail__tab-badge">Active</span>}
          </button>
        </div>
      )}

      {/* ── REQUEST DETAILS TAB ── */}
      {activeTab === 'details' && (
      <>

      {/* Basic Information */}
      <div className="request-detail__section">
        <h2 className="request-detail__section-title">Product Information</h2>
        <div className="request-detail__grid">
          <div className="request-detail__field">
            <label className="request-detail__label">Product/Capability Name</label>
            <p className="request-detail__value">
              {request.product?.product_name || request.legacy_product_name || 'N/A'}
            </p>
          </div>
          {request.product?.product_type && (
            <div className="request-detail__field">
              <label className="request-detail__label">Product Type</label>
              <p className="request-detail__value">{request.product.product_type}</p>
            </div>
          )}
          {request.product?.categorization && (
            <div className="request-detail__field">
              <label className="request-detail__label">Categorization</label>
              <p className="request-detail__value">{request.product.categorization}</p>
            </div>
          )}
          <div className="request-detail__field">
            <label className="request-detail__label">Request Type</label>
            <p className="request-detail__value">{TYPE_DISPLAY_NAMES[request.requestType?.type_code] || request.requestType?.type_name || request.legacy_request_type || 'N/A'}</p>
          </div>
          <div className="request-detail__field">
            <label className="request-detail__label">Owner</label>
            <p className="request-detail__value">
              {request.product?.owner
                ? `${request.product.owner.first_name} ${request.product.owner.last_name} (${request.product.owner.email})`
                : request.owner
                ? `${request.owner.first_name} ${request.owner.last_name} (${request.owner.email})`
                : 'Not assigned'}
            </p>
          </div>
          <div className="request-detail__field">
            <label className="request-detail__label">Created By</label>
            <p className="request-detail__value">
              {request.creator
                ? `${request.creator.first_name} ${request.creator.last_name} (${request.creator.email})`
                : 'N/A'}
            </p>
          </div>
          <div className="request-detail__field">
            <label className="request-detail__label">Created Date</label>
            <p className="request-detail__value">{formatDate(request.created_at)}</p>
          </div>
          <div className="request-detail__field">
            <label className="request-detail__label">Last Updated</label>
            <p className="request-detail__value">{formatDate(request.updated_at)}</p>
          </div>
          {request.submitted_at && (
            <div className="request-detail__field">
              <label className="request-detail__label">Submitted Date</label>
              <p className="request-detail__value">{formatDate(request.submitted_at)}</p>
            </div>
          )}
          {request.completed_at && (
            <div className="request-detail__field">
              <label className="request-detail__label">Completed Date</label>
              <p className="request-detail__value">{formatDate(request.completed_at)}</p>
            </div>
          )}
        </div>
      </div>

      {/* NPR: Rejection Notice */}
      {isNPRRequest(request) && request.status === 'Draft' && request.rejection_reason && (
        <div className="request-detail__section" style={{
          backgroundColor: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '20px',
        }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#9a3412', marginBottom: '8px' }}>
            Request Declined
          </h3>
          <p style={{ fontSize: '14px', color: '#7c2d12', marginBottom: '12px' }}>
            Your product request was declined by Product Operations. You can update the details below and resubmit.
          </p>
          <div style={{ backgroundColor: '#ffedd5', borderRadius: '4px', padding: '10px 14px', fontSize: '14px', color: '#7c2d12' }}>
            <strong>Reason:</strong> {request.rejection_reason}
          </div>
          <div style={{ marginTop: '12px' }}>
            <button
              onClick={handleEdit}
              className="request-detail__button request-detail__button--secondary"
            >
              Edit &amp; Resubmit
            </button>
          </div>
        </div>
      )}


      {/* NPR: Completed — View Product link */}
      {isNPRRequest(request) && request.status === 'Verified' && request.product_id && (
        <div className="request-detail__section" style={{
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '20px',
        }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#14532d', marginBottom: '6px' }}>
            Product Created
          </h3>
          <p style={{ fontSize: '14px', color: '#166534', marginBottom: '8px' }}>
            This request has been completed and the product has been added to the catalog.
          </p>
          <a
            href={`/products/${request.product_id}`}
            className="request-detail__button request-detail__button--secondary"
            style={{ display: 'inline-block', textDecoration: 'none' }}
          >
            View Product →
          </a>
        </div>
      )}


      {/* Delegation submit success banner */}
      {delegationSubmitSuccess && (
        <div className="request-detail__section" style={{
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '6px',
          padding: '14px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}>
          <p style={{ fontSize: '14px', color: '#166534', margin: 0 }}>
            ✅ <strong>Input submitted.</strong> Your answers have been saved to the request. The approver has been notified.
          </p>
          <button
            onClick={() => setDelegationSubmitSuccess(false)}
            style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer', fontSize: '16px', padding: '0 4px' }}
          >✕</button>
        </div>
      )}

      {/* Needs Input banner — shown when delegation is active */}
      {request.status === 'Needs Input' && (
        <div className="request-detail__section" style={{
          backgroundColor: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: '6px',
          padding: '16px 20px',
          marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#92400e', margin: '0 0 6px' }}>
                Contributions Requested
              </h3>
              <p style={{ fontSize: '14px', color: '#78350f', margin: 0, lineHeight: 1.5 }}>
                This request is waiting for input from assigned contributors.
                Editing is locked until contributions are complete or cancelled.
              </p>
            </div>
            {canManageDelegation(currentUser) && (
              <button
                onClick={handleCancelDelegation}
                disabled={cancellingDelegation}
                style={{
                  background: 'none', border: '1px solid #d97706', borderRadius: '6px',
                  padding: '6px 14px', fontSize: '13px', fontWeight: '500', color: '#92400e',
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                }}
              >
                {cancellingDelegation ? 'Cancelling…' : 'Cancel Contributions'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Delegate Input Panel — shown when Needs Input */}
      {request.status === 'Needs Input' && delegationRound && (
        <div className="request-detail__section">
          <DelegateInputPanel
            requestId={id}
            delegationRound={delegationRound}
            questions={questions}
            currentAnswers={request.answers}
            currentUserEmail={currentUser?.email}
            onSubmitted={handleDelegationSubmitted}
            onSwitchToDelegation={() => setActiveTab('delegation')}
          />
        </div>
      )}

      {/* Change Legend for CuC Requests */}
      {isCuCRequest(request) && fieldChanges && (
        <div className="request-detail__section" style={{
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
            Change Indicators
            {fieldChanges.priorSkiRelease && (
              <span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280', marginLeft: '8px' }}>
                (compared to {fieldChanges.priorSkiRelease.name})
              </span>
            )}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', fontSize: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '16px' }}>✓</span>
              <span>Field changed</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '16px' }}>✗</span>
              <span>Field unchanged</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '16px' }}>🕐</span>
              <span>{fieldChanges.isNewProduct ? 'New product' : 'New field'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '16px' }}>❓</span>
              <span>Cannot compare</span>
            </div>
          </div>
        </div>
      )}

      {/* Question Answers by Section */}
      {sections.length > 0 && sections.map((section, sectionIndex) => (
        <div key={sectionIndex} className="request-detail__section">
          <h2 className="request-detail__section-title">{section.name}</h2>
          <div className="request-detail__grid">
            {section.questions.map((question, questionIndex) => {
              const changeInfo = getFieldChangeInfo(question.question_key);
              const bgColor = getFieldBackgroundColor(question.question_key);
              const tooltip = getChangeTooltip(question.question_key);

              return (
                <div
                  key={questionIndex}
                  className="request-detail__field"
                  style={{
                    backgroundColor: bgColor,
                    padding: bgColor ? '12px' : undefined,
                    borderRadius: bgColor ? '4px' : undefined,
                    border: bgColor ? '1px solid rgba(0,0,0,0.05)' : undefined,
                  }}
                  title={tooltip}
                >
                  <label className="request-detail__label">
                    {question.question_text}
                    {question.is_required && <span className="request-detail__required"> *</span>}
                    {changeInfo && (
                      <span
                        style={{
                          marginLeft: '8px',
                          fontSize: '16px',
                          verticalAlign: 'middle',
                          cursor: 'help',
                        }}
                      >
                        {changeInfo.indicator}
                      </span>
                    )}
                  </label>
                  <p className="request-detail__value">
                    {renderAnswerValue(question, getAnswerValue(question.question_key))}
                  </p>
                  {question.help_text && (
                    <p className="request-detail__help-text">{question.help_text}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Empty State for No Questions/Answers */}
      {sections.length === 0 && (
        <div className="request-detail__section">
          <div className="request-detail__empty">
            <svg width="48" height="48" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <p>No intake responses have been provided yet</p>
            <button onClick={handleEdit} className="request-detail__button">
              Complete Intake Form
            </button>
          </div>
        </div>
      )}

      </> /* end details tab */
      )}

      {/* ── DELEGATION TAB ── */}
      {activeTab === 'delegation' && isDelegationEnabled(request) && (
        <>
          {/* Active round summary — read-only, for approvers and non-assigned viewers */}
          {delegationRound && (() => {
            const questionMap = {};
            questions.forEach(q => { questionMap[q.question_key] = q.question_text; });
            const AssignmentStatusBadge = ({ status }) => {
              const map = {
                pending:   { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
                submitted: { bg: '#dcfce7', color: '#15803d', label: 'Submitted' },
              };
              const s = map[status] || { bg: '#f3f4f6', color: '#6b7280', label: status };
              return (
                <span style={{ background: s.bg, color: s.color, borderRadius: '9999px', padding: '2px 10px', fontSize: '12px', fontWeight: '600' }}>
                  {s.label}
                </span>
              );
            };
            return (
              <div className="request-detail__section">
                <h2 className="request-detail__section-title">Active Delegation</h2>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                  Initiated by {delegationRound.initiated_by} on {new Date(delegationRound.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                </p>
                {delegationRound.general_notes && (
                  <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#78350f', marginBottom: '16px' }}>
                    <strong>Note to delegates:</strong> {delegationRound.general_notes}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {(delegationRound.assignments || []).map(a => (
                    <div key={a.id} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div>
                          <span style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>{a.group_label}</span>
                          <span style={{ fontSize: '13px', color: '#6b7280', marginLeft: '10px' }}>{a.assigned_to_email}</span>
                        </div>
                        <AssignmentStatusBadge status={a.status} />
                      </div>
                      {a.approver_note && (
                        <p style={{ fontSize: '13px', color: '#374151', marginBottom: '8px', fontStyle: 'italic' }}>"{a.approver_note}"</p>
                      )}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {(a.field_keys || []).map(k => (
                          <span key={k} style={{ background: '#f3f4f6', color: '#374151', borderRadius: '4px', padding: '2px 8px', fontSize: '12px' }}>
                            {questionMap[k] || k}
                          </span>
                        ))}
                      </div>
                      {a.status === 'submitted' && a.submitted_notes && (
                        <p style={{ fontSize: '13px', color: '#374151', marginTop: '8px' }}>
                          <strong>Delegate note:</strong> {a.submitted_notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                {canManageDelegation(currentUser) && (
                  <div style={{ marginTop: '16px' }}>
                    <button
                      onClick={handleCancelDelegation}
                      disabled={cancellingDelegation}
                      style={{ background: 'none', border: '1px solid #d97706', borderRadius: '6px', padding: '6px 14px', fontSize: '13px', fontWeight: '500', color: '#92400e', cursor: 'pointer' }}
                    >
                      {cancellingDelegation ? 'Cancelling…' : 'Cancel Contributions'}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Contribution History (all rounds) */}
          {delegationHistory.length > 0
            ? <DelegationHistoryPanel rounds={delegationHistory} />
            : (
              <div className="request-detail__section" style={{ color: '#6b7280', fontSize: '14px' }}>
                No contribution history yet.
              </div>
            )
          }
        </>
      )}

      {/* Field Delegation Modal */}
      {showDelegationModal && (
        <FieldDelegationModal
          requestId={id}
          onClose={() => setShowDelegationModal(false)}
          onSuccess={handleDelegationInitiated}
        />
      )}
    </div>
  );
};

export default RequestDetail;
